import { Button, Form, Modal } from "antd";
import { message } from "utils/ciq-message";
import { useContext, useEffect, useState } from "react";
import { ProjectContext } from "context/ProjectProvider";

import { useCIQMutation } from "hooks/ciq-gql-hooks";
import { MUTATION_CREATE_FEATURE_INSTANCE } from "entity-app/graphQL/ciq-feature-mutations";
import { gql } from "@apollo/client";
import MaterialFormBody from "./material-form-body";
import { MaterialExtractSpecType } from "../model";
import "./merge-create-material-modal.scss";

type MergeCreateMaterialModalProps = {
  open: boolean;
  onCancel: () => void;
  onComplete: () => void;
  selectedRows: MaterialExtractSpecType[];
  materialHeaderMap: any;
};

export default function MergeCreateMaterialModal({
  open,
  onCancel,
  onComplete,
  selectedRows,
  materialHeaderMap
}: MergeCreateMaterialModalProps) {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [selectedSpecIds, setSelectedSpecIds] = useState<string[]>([]);

  const { gqlClientForProject, tokenRetrievalState } =
    useContext(ProjectContext);

  const [createEntityMutation] = useCIQMutation(
    gql(MUTATION_CREATE_FEATURE_INSTANCE),
    { client: gqlClientForProject }
  );

  useEffect(() => {
    if (open && selectedRows?.length > 0) {
      const firstRow = selectedRows[0];
      form.setFieldsValue({
        title: firstRow.title || "",
        tag_name: firstRow.tag || "",
        manufacturer: firstRow.manufacturer || "",
        description: `Material Tags included: ${selectedRows
          .map((row) => row.tag)
          .filter(Boolean)
          .join(", ")}`
      });
      setSelectedSpecIds([]);
    }
  }, [open, selectedRows, form]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      const firstRow = selectedRows[0];

      const materialData: any = {
        extraction_data_id: firstRow.id,
        title: values.title,
        tag_name: values.tag_name || firstRow.tag,
        manufacturer: values.manufacturer,
        combined_extraction_ids: selectedRows
          .map((row) => row.id)
          .filter((id) => id !== firstRow.id),
        discription: values.description
      };

      if (values.assignee) {
        materialData.assignee = values.assignee;
      }
      if (values.gc_representative) {
        materialData.gc_representative = values.gc_representative;
      }
      if (selectedSpecIds.length > 0) {
        materialData.spec_sections = selectedSpecIds;
      }

      const variables = {
        feature_type: "MATERIALS",
        feature_instances: materialData
      };

      const res = await createEntityMutation({
        variables
      });

      if (!res.success) {
        message.error("Failed to create material");
      } else {
        message.success("Material created successfully");
        onComplete();
      }
    } catch (error) {
      console.error("Error creating material:", error);
    }
    setLoading(false);
  };

  return (
    <Modal
      className="merge-create-material-modal"
      title={
        <div className="flex items-baseline gap-1">
          <span className="text-base font-semibold text-ciq-black-a88">
            Merge &amp; Create Material
          </span>
          <span className="text-xs font-medium text-ciq-black/60 ml-1 mr-1">
            |
          </span>
          <span className="text-xs font-medium text-ciq-black/60">
            From {selectedRows.length} Selections
          </span>
        </div>
      }
      open={open}
      width={500}
      centered
      destroyOnHidden
      onCancel={onCancel}
      footer={[
        <section className="flex justify-end gap-2" key="footer">
          <Button onClick={onCancel} disabled={loading}>
            Cancel
          </Button>
          <Button
            type="primary"
            loading={loading}
            disabled={loading}
            onClick={handleSubmit}
          >
            Confirm &amp; Create
          </Button>
        </section>
      ]}
    >
      <p className="text-sm text-ciq-black-a88  mt-0">
        Selected extracted materials will be merged to add one Material to the
        Log.
      </p>
      <div className="overflow-y-auto max-h-[450px]">
        <Form form={form} layout="vertical" preserve={false}>
          <MaterialFormBody
            form={form}
            materialHeaderMap={materialHeaderMap}
            selectedSpecIds={selectedSpecIds}
            setSelectedSpecIds={setSelectedSpecIds}
            token={tokenRetrievalState?.token}
          />
        </Form>
      </div>
    </Modal>
  );
}
